import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { v1 as uuid } from 'uuid';
import classNames from 'classnames';
import { isIterableArray } from '../../helpers/utils';
import { DropdownItem, DropdownMenu, DropdownToggle, Media, UncontrolledDropdown } from 'reactstrap';
import Flex from './Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import cloudUpload from '../../assets/img/icons/cloud-upload.svg';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif'];
// HO-TASK-249: las cargas masivas también aceptan texto plano, y un .txt sin
// esto caía al icono genérico.
const SPREADSHEET_EXTENSIONS = ['xlsx', 'xls', 'csv', 'txt'];

const extensionOf = path => String(path || '').split('.').pop().toLowerCase();

// La preview usa <img src={base64}>, que solo puede decodificar imágenes: con
// un .xlsx el navegador mostraba el icono de imagen rota. Se mira el MIME que
// trae el archivo y, si viene vacío (pasa con archivos sin tipo conocido), se
// cae a la extensión para no perder la miniatura de las imágenes.
const isImageFile = ({ type, path }) =>
  type ? type.startsWith('image/') : IMAGE_EXTENSIONS.includes(extensionOf(path));

const isSpreadsheetFile = ({ type, path }) =>
  (type && (type.includes('spreadsheet') || type.includes('excel') || type === 'text/csv')) ||
  SPREADSHEET_EXTENSIONS.includes(extensionOf(path));

const getSize = size => {
  if (size < 1024) {
    return (
      <Fragment>
        <strong>{size}</strong> Byte
      </Fragment>
    );
  } else if (size < 1024 * 1024) {
    return (
      <Fragment>
        <strong>{(size / 1024).toFixed(2)}</strong> KB
      </Fragment>
    );
  } else {
    return (
      <Fragment>
        <strong>{(size / (1024 * 1024)).toFixed(2)}</strong> MB
      </Fragment>
    );
  }
}

const FalconDropzone = ({ placeholder = <img src={cloudUpload} alt="" width={25} className="mr-2" />, className, onChange, files = [], preview = false, ...rest }) =>
  <>
    <Dropzone
      onDrop={acceptedFiles => {
        const stringFiles = [];
        if (!!acceptedFiles.length) {
          acceptedFiles.map(file => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              stringFiles.push({
                id: uuid(),
                base64: reader.result,
                size: file.size,
                path: file.path,
                type: file.type,
                file: file 
              });
              onChange([...stringFiles]);
            }
            return true;
          });
        }
      }}
    >
      {({ getRootProps, getInputProps }) => (
        <div
          {...getRootProps({
            className: classNames(
              'p-3 border-dashed border-2x border-300 bg-light rounded-soft text-center cursor-pointer',
              className
            )
          })}
        >
          <input {...{ ...getInputProps(), ...rest }} />
          {placeholder}
        </div>
      )}
    </Dropzone>
    {preview && isIterableArray(files) && (
      <div className="border-top mt-3">
        {files.map(({ id, path, base64, size, type }) => (
          <Media className="align-items-center py-3 border-bottom btn-reveal-trigger" key={id}>
            {isImageFile({ type, path }) ? (
              <img className="img-fluid" width={38} src={base64} alt={path} />
            ) : (
              <div className="text-center text-600" style={{ width: 38 }}>
                <FontAwesomeIcon
                  icon={isSpreadsheetFile({ type, path }) ? faFileExcel : faFile}
                  className="fs-2"
                />
              </div>
            )}
            <Media body tag={Flex} justify="between" align="center" className="ml-3">
              <div className="flex-1 overflow-hidden">
                <h6 data-dz-name="" className="mb-1 text-truncate" title={path}>{path}</h6>
                <Flex className="position-relative" align="center">
                  <p className="mb-0 fs--1 text-400 line-height-1">{getSize(size)}</p>
                </Flex>
              </div>
              <UncontrolledDropdown className="text-sans-serif">
                <DropdownToggle color="link" size="sm" className="text-600 btn-reveal">
                  <FontAwesomeIcon icon="ellipsis-h" />
                </DropdownToggle>
                <DropdownMenu className="border py-0" right>
                  <div className="bg-white py-2">
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onChange(files.filter(file => file.id !== id))}
                    >
                      Eliminar archivo
                    </DropdownItem>
                  </div>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Media>
          </Media>
        ))}
      </div>
    )}
  </>

FalconDropzone.propTypes = {
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  files: PropTypes.array,
  preview: PropTypes.bool,
  isMulti: PropTypes.bool
};


export default FalconDropzone;
